import { IComboBoxOption } from '@fluentui/react';
import React, { FunctionComponent, useContext, useEffect, useState } from 'react';

import AppContext from 'App/AppContext';
import { IPCheckbox, IPLabel, IPSpinner, IPText } from 'baseComponents';
import { IPComboBox } from 'baseComponents/Combobox/IPComboBox';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import NormCoverageChart from 'components/Charts/NormCoverageChart';
import FrameworkAuditRunningIndicator from 'components/Copilot/FrameworkAudit/FrameworkAuditRunningIndicator';
import {
  isFrameworkAuditRunning,
  useActiveFrameworkAudits,
} from 'components/Copilot/FrameworkAudit/useActiveFrameworkAudits';
import KeyValueTagPicker from 'components/Pickers/KeyValueTagPicker';
import { KeyValueTags } from 'components/Tags/KeyValueTag';
import { globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import { NormCoverage } from 'models/dashboardScene';
import Norm from 'models/norm';
import Tag from 'models/tag';
import { useTranslation } from 'react-i18next';
import { apiGetWidgetDataNormCoverage } from 'services/Api/dashboardService';
import { apiRequest } from 'services/Auth/authConfig';
import { IWidgetConfigRendererProps } from '../WidgetConfigRenderer';
import { IWidgetRendererProps } from '../WidgetRenderer';

export class WidgetNormCoverageConfig {
  isoNormId: number;

  tagIds: number[];

  showTags: boolean;

  tags: Tag[];

  constructor() {
    this.isoNormId = -1;
    this.tagIds = [];
    this.showTags = false;
    this.tags = [];
  }

  load(raw: string | undefined) {
    if (raw) {
      try {
        const newRawConfig = JSON.parse(raw);
        this.isoNormId = newRawConfig.isoNormId ?? -1;
        this.tagIds = newRawConfig.tagIds ?? [];
        this.showTags = newRawConfig.showTags ?? false;
      } catch {
        //ignore
      }
    }
  }

  clone(): WidgetNormCoverageConfig {
    const newConfig = new WidgetNormCoverageConfig();
    newConfig.isoNormId = this.isoNormId;
    newConfig.tagIds = [...this.tagIds];
    newConfig.tags = [...this.tags];
    newConfig.showTags = this.showTags;

    return newConfig;
  }
}

interface IWidgetNormCoverageProps extends IWidgetRendererProps {}

const WidgetNormCoverage: FunctionComponent<IWidgetNormCoverageProps> = (props: IWidgetNormCoverageProps) => {
  const { t } = useTranslation(['translation', 'widgets']);
  const appContext = React.useContext(AppContext);
  const [data, setData] = React.useState<NormCoverage | undefined>(undefined);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [config, setConfig] = React.useState<WidgetNormCoverageConfig | undefined>(undefined);
  // Shared across every standard widget on the dashboard: one fetch, not one per widget.
  const { activeAudits } = useActiveFrameworkAudits();

  React.useEffect(() => {
    const loadConfig = (): WidgetNormCoverageConfig => {
      const newConfig = new WidgetNormCoverageConfig();
      newConfig.load(props.widget.widgetConfig);
      newConfig.tags = appContext.globalDataCache.tags.getItemsForId(newConfig.tagIds).filter((t) => t.tagId !== 0);

      return newConfig;
    };

    const loadData = async () => {
      try {
        if (isLoading) return;
        setIsLoading(true);

        await appContext.globalDataCache.tags.getItems();
        const config = loadConfig();
        setConfig(config);

        const accessToken = await appContext.getAccessToken(apiRequest.scopes);

        const data = await apiGetWidgetDataNormCoverage(config.isoNormId, config.tags, accessToken, props.abortSignal);

        setData(data);
      } catch (err) {
        appContext.setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  if (!data || isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (!data.isoNormId) {
    //config is invalid
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('widgets:NormCoverage.Config.Invalid')}</IPText>
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill horizontalAlign="center" tokens={globalStackTokensGapSmall}>
      {config && config.tags.length > 0 && config.showTags && (
        <IPStack.Item styles={{ root: { maxHeight: 44, overflowY: 'hidden' } }}>
          <KeyValueTags tags={config.tags} compact tagMaxWidth={200} />
        </IPStack.Item>
      )}
      <IPStack.Item>
        <IPText>{data.normName}</IPText>
      </IPStack.Item>
      <IPStack.Item>
        <FrameworkAuditRunningIndicator running={isFrameworkAuditRunning(activeAudits, data.isoNormId)} />
      </IPStack.Item>
      <IPStack.Item grow>
        <NormCoverageChart
          tags={config?.tags}
          normCoverageData={data}
          height={config && config.tags.length > 0 && config.showTags ? 160 : 220}
          width={280}
        ></NormCoverageChart>
      </IPStack.Item>
    </IPStack>
  );
};

export default WidgetNormCoverage;

//
// Config
//

interface IWidgetConfigNormCoverageChartProps extends IWidgetConfigRendererProps {}

export const WidgetConfigNormCoverage: FunctionComponent<IWidgetConfigNormCoverageChartProps> = (
  props: IWidgetConfigNormCoverageChartProps,
) => {
  const { t } = useTranslation(['widgets', 'translation', 'dashboard']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isoNorms, setISONorms] = useState<Norm[]>([]);
  const [selectedISONorm, setSelectedISONorm] = useState<Norm | undefined>(undefined);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [showTags, setShowTags] = useState<boolean>(false);
  const [currentWidgetId, setCurrentWidgetId] = useState<number>(0);

  useEffect(() => {
    if (currentWidgetId === 0 || currentWidgetId !== props.widget.widgetId) {
      setCurrentWidgetId(props.widget.widgetId);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadConfig = (): WidgetNormCoverageConfig => {
    let newRawConfig = new WidgetNormCoverageConfig();
    if (props.widget.widgetConfig) {
      newRawConfig = JSON.parse(props.widget.widgetConfig);
      newRawConfig.tags = appContext.globalDataCache.tags
        .getItemsForId(newRawConfig.tagIds)
        .filter((t) => t.tagId !== 0);
    }

    return newRawConfig;
  };

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      await appContext.globalDataCache.tags.getItems();
      const config = loadConfig();

      const isoNorms = appContext.globalDataCache.norms.getItemsISO();
      setISONorms(isoNorms.sort((a, b) => a.name.localeCompare(b.name)));

      if (config.isoNormId) {
        const norm = isoNorms.find((e) => e.isoNormId === config.isoNormId);
        setSelectedISONorm(norm);
      }

      if (config.tags) {
        setSelectedTags(config.tags);
      }

      setShowTags(config.showTags);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getConfig = (isoNormId: number | undefined, tags: Tag[], showTags: boolean): WidgetNormCoverageConfig => {
    const config = new WidgetNormCoverageConfig();
    config.isoNormId = isoNormId ?? 0;
    config.tagIds = tags.map((t) => t.tagId);
    config.showTags = showTags;

    return config;
  };

  const onSelectISONorm = (isoNormId: number) => {
    setSelectedISONorm(isoNorms.find((i) => i.isoNormId === isoNormId));
    const config = getConfig(isoNormId, selectedTags, showTags);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const getOptions = (): IComboBoxOption[] => {
    return isoNorms.map((n) => {
      return {
        key: n.isoNormId as number,
        text: n.name,
      };
    });
  };

  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  const onAddTag = (tag: Tag) => {
    const newTags = [...selectedTags, tag];
    setSelectedTags(newTags);
    const config = getConfig(selectedISONorm?.isoNormId, newTags, showTags);
    props.onUpdateConfig(JSON.stringify(config), selectedISONorm?.isoNormId !== undefined);
  };

  const onRemoveTag = (tag: Tag) => {
    const newTags = selectedTags.filter((t) => t.tagId !== tag.tagId);
    setSelectedTags(newTags);
    const config = getConfig(selectedISONorm?.isoNormId, newTags, showTags);
    props.onUpdateConfig(JSON.stringify(config), selectedISONorm?.isoNormId !== undefined);
  };

  const onSetShowTags = (checked: boolean) => {
    setShowTags(checked);
    const config = getConfig(selectedISONorm?.isoNormId, selectedTags, checked);
    props.onUpdateConfig(JSON.stringify(config), selectedISONorm?.isoNormId !== undefined);
  };

  if (getOptions().length === 0) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPLabel>{t('widgets:NormCoverage.Config.NoNorms')}</IPLabel>
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <IPLabel>{t('widgets:NormCoverage.Config.Label')}</IPLabel>
        <IPComboBox
          selectedKey={selectedISONorm?.isoNormId}
          onChange={(ev, option) => onSelectISONorm(option?.key as number)}
          options={getOptions()}
          useComboBoxAsMenuWidth
        ></IPComboBox>
      </IPStack.Item>
      <IPStack.Item>
        <IPLabel>{t('widgets:NormCoverage.Config.LabelTags')}</IPLabel>
        <KeyValueTagPicker
          itemLimit={5}
          selectedTags={selectedTags}
          onAdd={onAddTag}
          onRemove={onRemoveTag}
          isLoading={isLoading}
          allowCreate={false}
        ></KeyValueTagPicker>
      </IPStack.Item>
      <IPStack.Item>
        <IPCheckbox
          checked={showTags}
          label={t('widgets:NormCoverage.Config.LabelShowTags')}
          onChange={(ev, checked) => {
            onSetShowTags(checked ?? false);
          }}
        />
      </IPStack.Item>
    </IPStack>
  );
};
